import React from "react";
import { faEdit, faTrash, faComments, faBrain, faArrowTrendUp, faPlus, faBolt, faChartLine, faRocket, faLightbulb, faRobot, faUser, faHome, faCalendar, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Button from "components/base/Button";

import DealProfileCard from "components/cards/DealProfileCard";
import DealWorkflowPanel from "components/cards/DealWorkflowPanel";
import AIStatusIndicator from "components/common/AIStatusIndicator";

import PageBreadcrumb from "components/common/PageBreadcrumb";
import ConfirmDeleteModal from "components/modules/crm/common-components/ConfirmDeleteModal";
import DealDetailsTab from "components/modules/crm/deals-details/DealDetailsTab";
import DealModal from "components/modules/crm/deals/AddDealModalV2";
import InviteCustomerModal from "components/modals/InviteCustomerModal";
// import WorkflowProgressPanel from "components/cards/WorkflowProgressPanel"; // Disabled for deals - workflows are lead-specific
import { dealDetailsBreadcrumbItems } from "data/commonData";
import axiosInstance from "helpers/api";
import { useAtom } from "jotai";
import {
  dealDetailsMeetingsAtom,
  paginationAtom,
} from "jotai/DealJotaiInstance";

import _ from "lodash";
import { useEffect, useState } from "react";
import { Card, Col, Row, OverlayTrigger, Tooltip, Badge } from "react-bootstrap";
import toast from "react-hot-toast";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { user_info } from "jotai/UserJotaiInstance";
import Backdrop from "components/common/Backdrop";
import CustomLoader from "components/common/CustomLoader";
import { UserInfo } from "interfaces/user";
import { DealCardProps } from "interfaces/deal";
import classNames from "classnames";
import DealFloatingAI from "components/cards/DealFloatingAI";
import DealChatbotIntegration from "components/deals/DealChatbotIntegration";

// Extended from DealCardProps['deal'] to ensure compatibility with DealLLMContentSection
interface DealDetailsType {
  id: string | number;
  deal_name?: string;
  deal_price?: number;
  deal_state?: string;
  deal_lead_status?: string;
  deal_lead_type?: string;
  deal_closing_date?: string;
  deal_owner_id?: string;
  created_on?: string;
  deal_mls_id?: string;
  customers?: any[];
  related_deals?: any[];
  deal_inspection_period?: string;
  deal_meetings: any[];
  property_address?: string;
  deal_address?: string;
  deal_city?: string;
  deal_zip?: string;
  deal_owners?: any[];
  updated_on?: string;
}

const DealDetails = () => {
  const [dealDetails, setDealDetails] = useState<DealDetailsType>({
    id: "",
    deal_meetings: [],
  });

  const [, setDealDetailsMeeting] = useAtom(dealDetailsMeetingsAtom);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [callNotes, setCallNotes] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [relatedParties, setRelatedParties] = useState([]);
  const [notes, setNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  let { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const type = queryParams.get("type");
  const [pagination, setPagination] = useAtom(paginationAtom);
  const [activities, setActivities] = useState([]);
  const { limit, offset, queryType } = pagination;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [userInfo] = useAtom<UserInfo>(user_info);
  const isAuthorized = userInfo?.payment_status === "Cancelled" ? false : true;
  const [showDealDetails, setShowDealDetails] = useState(true);
  const [fetchedDealDetails, setFetchedDealDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activatingAI, setActivatingAI] = useState(false);
  const [workflowAlerts, setWorkflowAlerts] = useState<any[]>([]);
  const [workflowRefreshTrigger, setWorkflowRefreshTrigger] = useState(0);

  const fetchDealData = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`/deals/${id}`);
      if (response.data?.data?.deal) {
        setDealDetails(response.data.data.deal);
        setDealDetailsMeeting(response.data.data.deal.deal_meetings || []);
        setFetchedDealDetails(true);
      } else {
        console.error('Invalid response structure for deal data:', response.data);
        setFetchedDealDetails(false);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch deal data');
      setFetchedDealDetails(false);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchActivityData = async () => {
    try {
      setIsLoading(true);
      console.log(`[FRONTEND] 🎯 Fetching activities for deal ${id}`);
      
      const response = await axiosInstance.get(`/deals/${id}/activities`, {
        params: { limit, offset, queryType },
      });
      
      console.log(`[FRONTEND] ✅ Activities API response:`, response);
      console.log(`[FRONTEND] 📊 Response data:`, response?.data);
      
      // Add defensive checks for response structure
      if (response?.data?.data?.activities) {
        setActivities(response.data.data.activities);
        console.log(`[FRONTEND] 🎉 Set ${response.data.data.activities.length} activities`);
      } else {
        console.warn(`[FRONTEND] ⚠️ Unexpected response structure:`, response?.data);
        setActivities([]);
      }
      
      if (response?.data?.data?.pagination) {
        setPagination((prev) => ({
          ...prev,
          total: response.data.data.pagination.total,
        }));
      }
    } catch (error: any) {
      console.error(`[FRONTEND] ❌ Error fetching activities:`, error);
      console.error(`[FRONTEND] ❌ Error response:`, error?.response?.data);
      setActivities([]);
    } finally {
      setIsLoading(false);
    }
  };
  const fetchCallNotes = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`/deals/${id}/activities`, {
        params: { type: "call_note" },
      });
      setCallNotes(response.data.data.notes);
    } catch (error: any) {
      console.log(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNotes = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`/deals/${id}/activities`, {
        params: { type: "note" },
      });

      setNotes(response.data.data.notes);
    } catch (error: any) {
      console.log(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };
  const fetchTaskData = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`/deals/${id}/activities`, {
        params: { type: "task" },
      });

      setTasks(response.data.data.tasks);
    } catch (error: any) {
      console.log(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRelatedParties = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`/deals/${id}/related-parties`);
      
      // Handle different response structures
      let parties = [];
      if (response.data?.data && Array.isArray(response.data.data)) {
        parties = response.data.data; // Old format
      } else if (response.data && Array.isArray(response.data)) {
        parties = response.data; // New format (parties directly in response.data)
      } else {
        console.error('Invalid response structure for related parties:', response.data);
      }
      
      console.log(`🔍 [RELATED PARTIES] Fetched ${parties.length} parties:`, parties);
      setRelatedParties(parties);
    } catch (error: any) {
      console.error('❌ [RELATED PARTIES] Fetch error:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch related parties');
      setRelatedParties([]);
    } finally {
      setIsLoading(false);
    }
  };
  const fetchAttachmentData = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`/deals/${id}/activities`, {
        params: { type: "attachments" },
      });

      if (response.data?.data?.attachments) {
        setAttachments(response.data.data.attachments);
      } else {
        console.error('Invalid response structure for attachment data:', response.data);
        setAttachments([]);
      }
    } catch (error: any) {
      console.log(error.response?.data?.message || 'Failed to fetch attachment data');
      setAttachments([]);
    } finally {
      setIsLoading(false);
    }
  };
  const fetchSearchActivityResults = async (searchQuery: string) => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`/deals/${id}/activities`, {
        params: { q: searchQuery },
      });
      setActivities(response.data.data.activities);
    } catch (err) {
      toast.error("Error fetching activities");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSearchTaskResults = async (searchQuery: string) => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get(`/deals/${id}/activities`, {
        params: { q: searchQuery, type: "task" },
      });
      setTasks(response.data.data.tasks);
    } catch (err) {
      toast.error("Error fetching tasks");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteDeal = async (id: any) => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.delete(`/deals/${id}`);
      toast.success(response.data.message);
      navigate("/deals");
    } catch (error: any) {
      toast.error(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
    setShowDeleteModal(false);
  };

  useEffect(() => {
    (async () => {
      await fetchDealData();
    })();
  }, []);

  useEffect(() => {
    if (type === "" || type === "activity") {
      (async () => {
        await fetchActivityData();
      })();
    }
  }, [type, limit, offset]);

  useEffect(() => {
    if (type === "call") {
      (async () => {
        await fetchCallNotes();
      })();
    }
  }, [type]);



  useEffect(() => {
    if (type === "notes") {
      (async () => {
        await fetchNotes();
      })();
    }
  }, [type]);

  useEffect(() => {
    if (type === "task") {
      (async () => {
        await fetchTaskData();
      })();
    }
  }, [type]);

  useEffect(() => {
    if (type === "related parties") {
      (async () => {
        await fetchRelatedParties();
      })();
    }
  }, [type]);

  useEffect(() => {
    if (type === "attachments") {
      (async () => {
        await fetchAttachmentData();
      })();
    }
  }, [type]);

  const handleSearchInputChange = _.debounce((searchQuery: any) => {
    fetchSearchActivityResults(searchQuery.target.value);
  }, 500);



  const handleSearchTask = _.debounce((searchQuery: any) => {
    fetchSearchTaskResults(searchQuery.target.value);
  }, 500);

  useEffect(() => {
    if (!isAuthorized) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "childList") {
            const backdropExists = document.getElementById("kairos-backdrop");
            if (!backdropExists) {
              setShowDealDetails(false);
            }
          }
        });
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
      return () => observer.disconnect();
    }
  }, [isAuthorized]);

  const activateAICoordinator = async () => {
    if (!dealDetails?.id) {
      toast.error('Deal ID not found');
      return;
    }

    try {
      setActivatingAI(true);
      
      // Call API to activate AI coordinator
      const response = await axiosInstance.post(`/deals/${dealDetails.id}/activate-ai-coordinator`);
      
      if (response.data.success) {
        toast.success('🚀 AI Coordinator activated! Handling 47+ tasks automatically.');
        
        // Refresh the deal data to show updated status
        await fetchDealData();
        
        // Trigger workflow refresh to show AI coordinator impact
        setWorkflowRefreshTrigger(prev => prev + 1);
      } else {
        toast.error('Failed to activate AI coordinator');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to activate AI coordinator');
    } finally {
      setActivatingAI(false);
    }
  };

  return (
    <>
      {fetchedDealDetails ? (
        <>
          {(isAuthorized || showDealDetails) && (
            <div className="ai-deal-details-container">
              <div className="mb-9">
                <Row className="align-items-center justify-content-between g-3 mb-4">
                  <Col md="auto">
                    <PageBreadcrumb items={dealDetailsBreadcrumbItems} />
                  </Col>
                </Row>
                
                {/* AI Intelligence Header */}
                <div 
                  className="intelligence-header mb-4"
                  style={{
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                    borderRadius: '16px',
                    padding: '24px',
                    border: '1px solid rgba(0, 212, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <div>
                    {/* Top Section */}
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="d-flex align-items-center">
                        <div style={{ position: 'relative', marginRight: '20px' }}>
                          <FontAwesomeIcon 
                            icon={faRocket} 
                            style={{ 
                              color: '#00d4ff', 
                              fontSize: '36px',
                              filter: 'drop-shadow(0 0 12px rgba(0, 212, 255, 0.6))' 
                            }} 
                          />
                        </div>
                        <div>
                          <h2 style={{ 
                            color: '#1f2937', 
                            fontWeight: 600, 
                            marginBottom: '8px',
                            textShadow: '0 0 20px rgba(0, 212, 255, 0.3)',
                            fontSize: '1.8rem'
                          }}>
                            {dealDetails?.deal_name || 'Deal Intelligence Center'}
                          </h2>
                          <div className="d-flex align-items-center gap-3">
                            <Badge 
                              bg="primary"
                              style={{
                                background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '12px',
                                padding: '6px 12px',
                                fontWeight: '600'
                              }}
                            >
                              {dealDetails?.deal_lead_status || 'Active'}
                            </Badge>
                            <div 
                              className="price-display d-flex align-items-center"
                              style={{
                                background: 'rgba(16, 185, 129, 0.1)',
                                borderRadius: '12px',
                                padding: '6px 12px',
                                border: '1px solid rgba(16, 185, 129, 0.2)'
                              }}
                            >
                              <FontAwesomeIcon icon={faChartLine} className="text-success me-2" />
                              <span className="fw-bold text-success" style={{ fontSize: '14px' }}>
                                ${dealDetails?.deal_price?.toLocaleString() || '0'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    
                      {/* AI Features - Top Right */}
                      <div className="d-flex align-items-center gap-2">
                        {/* AI Coordinator Activation/Status */}
                        {dealDetails?.deal_owners?.some((owner: any) => owner.deal_owner_role === 'TransactionCoordinator') ? (
                          <div 
                            className="d-flex align-items-center"
                            style={{
                              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                              padding: '10px 20px',
                              borderRadius: '24px',
                              border: '1px solid rgba(16, 185, 129, 0.3)'
                            }}
                          >
                            <FontAwesomeIcon 
                              icon={faRobot} 
                              className="me-2" 
                              style={{ 
                                color: '#10b981',
                                fontSize: '18px',
                                filter: 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.6))'
                              }}
                            />
                            <div>
                              <span 
                                className="fw-bold d-block"
                                style={{ 
                                  fontSize: '13px',
                                  color: '#059669'
                                }}
                              >
                                AI Coordinator Active
                              </span>
                              <span 
                                className="text-muted"
                                style={{ 
                                  fontSize: '11px'
                                }}
                              >
                                Handling 47+ tasks
                              </span>
                            </div>
                          </div>
                        ) : (
                          <Button
                            variant="primary"
                            className="ai-activation-header-btn"
                            onClick={activateAICoordinator}
                            disabled={activatingAI}
                            style={{
                              background: 'linear-gradient(135deg, #00d4ff 0%, #10b981 100%)',
                              border: 'none',
                              borderRadius: '24px',
                              padding: '10px 20px',
                              fontWeight: '600',
                              fontSize: '13px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              boxShadow: '0 4px 16px rgba(0, 212, 255, 0.3)',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 212, 255, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 212, 255, 0.3)';
                            }}
                          >
                            <FontAwesomeIcon icon={faBolt} />
                            {activatingAI ? 'Activating...' : 'Activate AI Coordinator'}
                          </Button>
                        )}

                        {/* Invite Customer Button */}
                        <Button
                          variant="primary"
                          className="ai-activation-header-btn ms-2"
                          onClick={() => setShowInviteModal(true)}
                          style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            borderRadius: '24px',
                            padding: '10px 20px',
                            fontSize: '13px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 16px rgba(102, 126, 234, 0.3)';
                          }}
                        >
                          <FontAwesomeIcon icon={faEnvelope} />
                          Invite Customer
                        </Button>
                        
                        {/* Dynamic AI Status Indicator */}
                        <AIStatusIndicator 
                          dealId={dealDetails?.id?.toString() || ''}
                          hasAICoordinator={dealDetails?.deal_owners?.some((owner: any) => owner.deal_owner_role === 'TransactionCoordinator')}
                          currentStage={dealDetails?.deal_lead_status}
                          lastActivity={dealDetails?.updated_on ? new Date(dealDetails.updated_on) : 
                                       dealDetails?.created_on ? new Date(dealDetails.created_on) : undefined}
                        />
                      </div>
                    </div>

                    {/* Bottom Section */}
                    <div className="d-flex align-items-end justify-content-between">
                      {/* Additional Deal Info */}
                      <div className="d-flex align-items-center gap-4">
                        {/* Property Address */}
                        {(dealDetails?.property_address || dealDetails?.deal_address || dealDetails?.deal_state) && (
                          <span style={{ fontSize: '14px', color: '#6b7280' }}>
                            <FontAwesomeIcon icon={faHome} className="me-1" style={{ color: '#00d4ff' }} />
                            {[
                              dealDetails?.property_address || dealDetails?.deal_address,
                              dealDetails?.deal_city,
                              dealDetails?.deal_state,
                              dealDetails?.deal_zip
                            ].filter(Boolean).join(', ')}
                          </span>
                        )}
                        {/* Customer Name */}
                        {dealDetails?.customers?.[0] && (
                          <span style={{ fontSize: '14px', color: '#6b7280' }}>
                            <FontAwesomeIcon icon={faUser} className="me-1" style={{ color: '#00d4ff' }} />
                            {dealDetails.customers[0].first_name || dealDetails.customers[0].customer_first_name} {dealDetails.customers[0].last_name || dealDetails.customers[0].customer_last_name}
                          </span>
                        )}
                        {/* MLS ID */}
                        {dealDetails?.deal_mls_id && (
                          <span style={{ fontSize: '14px', color: '#6b7280' }}>
                            <FontAwesomeIcon icon={faHome} className="me-1" style={{ color: '#00d4ff' }} />
                            MLS: {dealDetails.deal_mls_id}
                          </span>
                        )}
                        {/* Closing Date */}
                        {dealDetails?.deal_closing_date && (
                          <span style={{ fontSize: '14px', color: '#6b7280' }}>
                            <FontAwesomeIcon icon={faCalendar} className="me-1" style={{ color: '#00d4ff' }} />
                            Closing: {new Date(dealDetails.deal_closing_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>

                      {/* Edit/Delete Actions - Bottom Right */}
                      <div className="d-flex align-items-center gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => setShowEditModal(true)}
                          style={{
                            border: '1px solid rgba(102, 126, 234, 0.3)',
                            borderRadius: '12px',
                            padding: '6px 14px',
                            fontSize: '12px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s ease',
                            background: 'rgba(255, 255, 255, 0.8)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.border = '1px solid transparent';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)';
                            e.currentTarget.style.color = 'inherit';
                            e.currentTarget.style.border = '1px solid rgba(102, 126, 234, 0.3)';
                          }}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                          <span>Edit</span>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => setShowDeleteModal(true)}
                          style={{
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '12px',
                            padding: '6px 14px',
                            fontSize: '12px',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s ease',
                            background: 'rgba(255, 255, 255, 0.8)'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.border = '1px solid transparent';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.8)';
                            e.currentTarget.style.color = 'inherit';
                            e.currentTarget.style.border = '1px solid rgba(239, 68, 68, 0.3)';
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                          <span>Delete</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Neural Network Background Pattern */}
                  <div 
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `
                        radial-gradient(circle at 20% 20%, rgba(0, 212, 255, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 80% 80%, rgba(102, 126, 234, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 50% 50%, rgba(118, 75, 162, 0.05) 0%, transparent 70%)
                      `,
                      pointerEvents: 'none'
                    }}
                  />
                </div>

                {/* Main Deal Content - Full Width */}
                <div className="modern-deal-details-layout">
                  {/* Enhanced Left Sidebar with AI Glass Effect */}
                  <div className="deal-details-sidebar">
                    <div className="sidebar-content">
                      {/* Deal Profile Card - Commented out to move info to top banner */}
                      {/*
                      <div 
                        className="ai-card-wrapper mb-3"
                        style={{
                          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                          borderRadius: '16px',
                          border: '1px solid rgba(0, 212, 255, 0.2)',
                          backdropFilter: 'blur(15px)',
                          overflow: 'hidden'
                        }}
                      >
                        <DealProfileCard
                          dealDetails={dealDetails}
                        />
                      </div>
                      */}
                      
                      {/* Deal Workflow Panel - Now the primary sidebar content */}
                      <div 
                        className="ai-card-wrapper"
                        style={{
                          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                          borderRadius: '16px',
                          border: '1px solid rgba(0, 212, 255, 0.2)',
                          backdropFilter: 'blur(15px)',
                          overflow: 'hidden'
                        }}
                      >
                        <DealWorkflowPanel
                          dealId={dealDetails?.id?.toString() || ''}
                          dealDetails={dealDetails}
                          refreshTrigger={workflowRefreshTrigger}
                          onWorkflowAlerts={setWorkflowAlerts}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Enhanced Main Content Area with AI Glass Effect */}
                  <div 
                    className="deal-details-main-content"
                    style={{
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
                      borderRadius: '16px',
                      border: '1px solid rgba(0, 212, 255, 0.2)',
                      backdropFilter: 'blur(15px)',
                      boxShadow: '0 8px 32px rgba(0, 212, 255, 0.1)',
                      overflow: 'hidden'
                    }}
                  >
                    <DealDetailsTab
                      dealDetails={dealDetails}
                      isLoading={isLoading}
                      tasks={tasks}
                      notes={notes}
                      activities={activities}
                      callNotes={callNotes}
                      attachmentsLv={attachments}
                      handleSearchInputChange={handleSearchInputChange}
                      handleSearchTask={handleSearchTask}
                      relatedParties={relatedParties}
                      onRefreshDeal={fetchDealData}
                    />
                  </div>
                </div>

                {/* Enhanced AI Assistant with Cache Preloading and Visual Indicators */}
                <DealChatbotIntegration
                  dealId={dealDetails?.id?.toString() || ''}
                  userId={userInfo?.id?.toString() || ''}
                  dealStage={(dealDetails as any)?.deal_stage?.deal_stage_name || dealDetails?.deal_lead_status}
                />
                

              </div>
              {/* <LeadDetailsOffcanvas open={openOffcanvas} setOpen={setOpenOffcanvas} /> */}
              <ConfirmDeleteModal
                modalShow={showDeleteModal}
                setModalShow={setShowDeleteModal}
                type="Lead"
                // @ts-ignore
                handleDelete={() => handleDeleteDeal(dealDetails?.id)}
              />

              <DealModal
                show={showEditModal}
                onHide={() => {
                  setShowEditModal(false);
                  // Refresh deal details after editing
                  fetchDealData();
                }}
                dealId={dealDetails?.id?.toString()}
                mode="edit"
              />

              <InviteCustomerModal
                show={showInviteModal}
                onHide={() => setShowInviteModal(false)}
                dealId={dealDetails?.id?.toString() || ''}
                dealName={dealDetails?.deal_name}
                dealDetails={dealDetails}
              />
              
              {/* AI-Enhanced Deal Details Styles */}
              <style dangerouslySetInnerHTML={{
                __html: `
                /* AI Color System Variables */
                :root {
                  --ai-primary: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                  --ai-accent: #00d4ff;
                  --ai-glow: rgba(0, 212, 255, 0.3);
                  --ai-success: #10b981;
                  --ai-warning: #f59e0b;
                  --ai-danger: #ef4444;
                  --ai-info: #3b82f6;
                  --ai-surface: rgba(255, 255, 255, 0.05);
                  --ai-surface-elevated: rgba(255, 255, 255, 0.1);
                  --ai-glass-bg: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
                  --ai-text-primary: #1f2937;
                  --ai-text-secondary: #6b7280;
                  --ai-border-primary: rgba(0, 212, 255, 0.2);
                  --deal-sidebar-width: 380px;
                }

                .ai-deal-details-container {
                  min-height: 100vh;
                  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
                  position: relative;
                }

                .ai-deal-details-container::before {
                  content: '';
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  background: 
                    radial-gradient(circle at 10% 20%, rgba(0, 212, 255, 0.05) 0%, transparent 40%),
                    radial-gradient(circle at 80% 80%, rgba(102, 126, 234, 0.05) 0%, transparent 40%),
                    radial-gradient(circle at 40% 40%, rgba(118, 75, 162, 0.03) 0%, transparent 40%);
                  pointer-events: none;
                }

                .modern-deal-details-layout {
                  display: flex;
                  gap: 2rem;
                  min-height: calc(100vh - 200px);
                  position: relative;
                  animation: fadeInUp 0.6s ease-out;
                }

                @keyframes fadeInUp {
                  from {
                    opacity: 0;
                    transform: translateY(20px);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }

                /* AI Pulse Animation */
                @keyframes pulse {
                  0%, 100% { opacity: 1; }
                  50% { opacity: 0.7; }
                }

                @keyframes ai-glow-pulse {
                  0%, 100% { 
                    box-shadow: 0 0 20px rgba(0, 212, 255, 0.4);
                    transform: scale(1);
                  }
                  50% { 
                    box-shadow: 0 0 40px rgba(0, 212, 255, 0.6);
                    transform: scale(1.05);
                  }
                }

                .deal-details-sidebar {
                  width: var(--deal-sidebar-width);
                  flex-shrink: 0;
                  position: sticky;
                  top: 2rem;
                  height: fit-content;
                  max-height: calc(100vh - 4rem);
                  overflow-y: auto;
                  scrollbar-width: none;
                  -ms-overflow-style: none;
                }

                .deal-details-sidebar::-webkit-scrollbar {
                  display: none;
                }

                .sidebar-content {
                  display: flex;
                  flex-direction: column;
                  gap: 1rem;
                }

                .ai-card-wrapper {
                  transition: all 0.3s ease;
                  position: relative;
                }

                .ai-card-wrapper:hover {
                  transform: translateY(-2px);
                  box-shadow: 0 12px 40px rgba(0, 212, 255, 0.15);
                }

                .ai-card-wrapper::before {
                  content: '';
                  position: absolute;
                  top: -2px;
                  left: -2px;
                  right: -2px;
                  bottom: -2px;
                  background: linear-gradient(135deg, var(--ai-accent), transparent);
                  border-radius: 18px;
                  opacity: 0;
                  transition: opacity 0.3s ease;
                  z-index: -1;
                }

                .ai-card-wrapper:hover::before {
                  opacity: 0.3;
                }

                .deal-details-main-content {
                  flex: 1;
                  min-width: 0;
                  transition: all 0.3s ease;
                }

                .modern-ai-card {
                  transition: all 0.3s ease !important;
                }

                .modern-ai-card:hover {
                  transform: translateY(-2px);
                  box-shadow: 0 12px 40px rgba(0, 212, 255, 0.15) !important;
                }

                /* Enhanced Floating AI Assistant */
                .floating-ai-assistant {
                  position: fixed;
                  bottom: 2rem;
                  right: 2rem;
                  width: 380px;
                  z-index: 1050;
                  overflow: hidden;
                  transition: all 0.3s ease;
                  animation: slideInRight 0.4s ease-out;
                }

                @keyframes slideInRight {
                  from {
                    transform: translateX(100%);
                    opacity: 0;
                  }
                  to {
                    transform: translateX(0);
                    opacity: 1;
                  }
                }

                .floating-ai-assistant.minimized {
                  height: 72px !important;
                }

                .chatbot-header {
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                }

                .chatbot-controls {
                  display: flex;
                  align-items: center;
                }

                .chatbot-content {
                  height: 420px;
                  display: flex;
                  flex-direction: column;
                }

                .chat-messages {
                  flex: 1;
                  overflow-y: auto;
                  scrollbar-width: thin;
                  scrollbar-color: rgba(0, 212, 255, 0.2) transparent;
                }

                .chat-messages::-webkit-scrollbar {
                  width: 6px;
                }

                .chat-messages::-webkit-scrollbar-track {
                  background: transparent;
                }

                .chat-messages::-webkit-scrollbar-thumb {
                  background: rgba(0, 212, 255, 0.2);
                  border-radius: 3px;
                }

                .chat-messages::-webkit-scrollbar-thumb:hover {
                  background: rgba(0, 212, 255, 0.3);
                }

                .floating-ai-toggle {
                  z-index: 1049;
                }

                /* Intelligence Header Enhancement */
                .intelligence-header {
                  animation: fadeIn 0.6s ease-out;
                }

                @keyframes fadeIn {
                  from {
                    opacity: 0;
                  }
                  to {
                    opacity: 1;
                  }
                }

                /* Responsive Design */
                @media (max-width: 1200px) {
                  .modern-deal-details-layout {
                    flex-direction: column;
                  }

                  .deal-details-sidebar {
                    width: 100%;
                    position: relative;
                    top: 0;
                    max-height: none;
                  }
                }

                @media (max-width: 768px) {
                  .intelligence-header {
                    padding: 16px !important;
                  }
                  
                  .intelligence-header h2 {
                    font-size: 1.3rem !important;
                  }
                  
                  .floating-ai-assistant {
                    width: 90%;
                    left: 5%;
                    right: 5%;
                  }
                  
                  .ai-action-btn {
                    padding: 8px 16px !important;
                    font-size: 14px !important;
                  }
                  
                  .deal-details-sidebar {
                    padding: 0 1rem;
                  }
                `
              }} />
            </div>
          )}



          {!isAuthorized && <Backdrop />}
        </>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "90vh" }}
        >
          <CustomLoader />
        </div>
      )}
    </>
  );
};

export default DealDetails;
